import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { apiGet } from "../../../api/apiGet";
import { apiPost } from "../../../api/apiPost";
import { apiDelete } from "../../../api/apiDelete";
import { apiPut } from "../../../api/apiPut";
import { IMAGE_TYPES } from "../../../helpers/const";

// Thunk for creating a new user (admin action)
export const createUser = createAsyncThunk(
  "adminSlice/createUser",
  async (userData, thunkAPI) => {
    try {
      const response = await apiPost.createUser(userData);
      return response.data;
    } catch (err) {
      // Return backend error details if available
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.detail,
        errors: err?.response?.data,
      });
    }
  },
);

export const getAdminVars = createAsyncThunk(
  "adminSlice/getAdminVars",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getAdminVars();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const saveAdminImage = createAsyncThunk(
  "adminSlice/saveAdminImage",
  async (data, thunkAPI) => {
    try {
      const response = await apiPost.saveAdminImage(data);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const updateAdminImage = createAsyncThunk(
  "adminSlice/updateAdminImage",
  async (data, thunkAPI) => {
    try {
      const response = await apiPut.updateImageByKey(data);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const removeAdminImage = createAsyncThunk(
  "adminSlice/removeAdminImage",
  async ({ id }, thunkAPI) => {
    try {
      const response = await apiDelete.deleteAdminImage({ id });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

export const updateSiteProperties = createAsyncThunk(
  "adminSlice/updateSiteProperties",
  async ({ newPrimary, newSecondary, title, allow_approval }, thunkAPI) => {
    try {
      const response = await apiPut.updateSiteProperties({
        primary_color: newPrimary,
        secondary_color: newSecondary,
        title,
        allow_approval,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

// API Request Logs thunks
export const getApiRequestLogs = createAsyncThunk(
  "adminSlice/getApiRequestLogs",
  async (params = {}, thunkAPI) => {
    try {
      const response = await apiGet.getApiRequestLogs(params);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.detail || "Failed to fetch API logs",
      });
    }
  },
);

export const getApiRequestLogDetail = createAsyncThunk(
  "adminSlice/getApiRequestLogDetail",
  async (logId, thunkAPI) => {
    try {
      const response = await apiGet.getApiRequestLogDetail(logId);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.detail || "Failed to fetch log details",
      });
    }
  },
);

export const getEmailForTemplate = createAsyncThunk(
  "adminSlice/getEmailForTemplate",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getTemplateUsers();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
      });
    }
  },
);

// Vendor Categories thunks
export const getActiveVendorCategories = createAsyncThunk(
  "adminSlice/getActiveVendorCategories",
  async (_, thunkAPI) => {
    try {
      const response = await apiGet.getActiveVendorCategories();
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail:
          err?.response?.data?.detail || "Failed to fetch vendor categories",
      });
    }
  },
);

export const updateVendorCategories = createAsyncThunk(
  "adminSlice/updateVendorCategories",
  async ({ userId, vendorCategories }, thunkAPI) => {
    try {
      const response = await apiPut.updateVendorCategories(
        userId,
        vendorCategories,
      );
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail:
          err?.response?.data?.detail ||
          err?.response?.data?.error ||
          "Failed to update vendor categories",
      });
    }
  },
);

// User Management thunks
export const getUserById = createAsyncThunk(
  "adminSlice/getUserById",
  async (userId, thunkAPI) => {
    try {
      const response = await apiGet.getUserById(userId);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.detail || "Failed to fetch user details",
      });
    }
  },
);

export const fetchCurrentVersion = createAsyncThunk(
  "adminSlice/fetchCurrentVersion",
  async (name = "CSFA", thunkAPI) => {
    try {
      const response = await apiGet.getVersionByName(name);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.error || "Failed to fetch version",
      });
    }
  },
);

export const checkNewVersionAvailable = createAsyncThunk(
  "adminSlice/checkNewVersionAvailable",
  async ({ name = "CSFA", currentVersion }, thunkAPI) => {
    try {
      const response = await apiGet.checkNewVersionAvailable({
        name,
        currentVersion,
      });
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.error || "Failed to check version",
      });
    }
  },
);

export const loginAsUser = createAsyncThunk(
  "adminSlice/loginAsUser",
  async (userId, thunkAPI) => {
    try {
      const response = await apiPost.loginAsUser(userId);
      return response.data;
    } catch (err) {
      return thunkAPI.rejectWithValue({
        code: err?.response?.data?.code,
        detail: err?.response?.data?.detail || "Failed to login as user",
        error: err?.response?.data?.error,
      });
    }
  },
);

export const adminSlice = createSlice({
  name: "adminSlice",
  initialState: {
    adminActive: false,
    primaryColor: "",
    secondaryColor: "",
    adminLoading: true,
    promoImages: [],
    allPromoImages: [],
    createUserLoading: false,
    createUserError: null,
    createUserSuccess: false,
    userAdmin: false,
    // API Logs state
    apiLogs: [],
    apiLogsLoading: false,
    apiLogsError: null,
    selectedLogDetail: null,
    logDetailLoading: false,
    logDetailError: null,
    // Vendor Categories state
    vendorCategories: [],
    vendorCategoriesLoading: false,
    vendorCategoriesError: null,
    updateVendorCategoriesLoading: false,
    updateVendorCategoriesError: null,
    // User Management state
    selectedUser: null,
    selectedUserLoading: false,
    selectedUserError: null,
    // Login As state
    loginAsLoading: false,
    loginAsError: null,
    loginAsSuccess: false,
    titleImage: null,
    logoImage: null,
    backgroundImage: null,
    title: "",
    allowApproval: "N",
    viewCarts: false,
    updateCarts: false,
    submitOrders: false,
    templateUsers: [],
    currentVersion: null,
    updateAvailable: false,
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Create User
      .addCase(createUser.pending, (state) => {
        state.createUserLoading = true;
        state.createUserError = null;
        state.createUserSuccess = false;
      })
      .addCase(createUser.fulfilled, (state, action) => {
        state.createUserLoading = false;
        state.createUserSuccess = true;
        state.createUserError = null;
      })
      .addCase(createUser.rejected, (state, action) => {
        state.createUserLoading = false;
        state.createUserSuccess = false;
        state.createUserError =
          action.payload?.detail || "Failed to create user.";
      })
      .addCase(getAdminVars.rejected, (state, action) => {
        state.adminLoading = false;
      })
      .addCase(getAdminVars.fulfilled, (state, action) => {
        return {
          ...state,
          primaryColor: action.payload.primary_color,
          secondaryColor: action.payload.secondary_color,
          adminActive: action.payload.site_admin,
          userAdmin: action.payload.user_admin,
          promoImages: action.payload.promo_images || [],
          allPromoImages: action.payload.all_promo_images || [],
          titleImage: action.payload.title_image,
          logoImage: action.payload.logo_image,
          backgroundImage: action.payload.background_image,
          title: action.payload.title || "",
          allowApproval: action.payload.allow_approval || "N",
          adminLoading: false,
        };
      })
      .addCase(saveAdminImage.fulfilled, (state, action) => {
        state.promoImages = action.payload.promo_images || state.promoImages;
        state.allPromoImages = action.payload.all_promo_images || state.allPromoImages;
        state.titleImage = action.payload.title_image;
        state.logoImage = action.payload.logo_image;
        state.backgroundImage = action.payload.background_image;
      })
      .addCase(removeAdminImage.fulfilled, (state, action) => {
        state.promoImages = action.payload.promo_images || state.promoImages;
        state.allPromoImages = action.payload.all_promo_images || state.allPromoImages;
        state.titleImage = action.payload.title_image;
        state.logoImage = action.payload.logo_image;
        state.backgroundImage = action.payload.background_image;
      })
      .addCase(updateAdminImage.fulfilled, (state, action) => {
        state.promoImages = action.payload.promo_images || state.promoImages;
        state.allPromoImages = action.payload.all_promo_images || state.allPromoImages;
        state.titleImage = action.payload.title_image;
        state.logoImage = action.payload.logo_image;
        state.backgroundImage = action.payload.background_image;
      })
      .addCase(updateSiteProperties.fulfilled, (state, action) => {
        return {
          ...state,
          primaryColor: action.payload.primary_color,
          secondaryColor: action.payload.secondary_color,
          allowApproval: action.payload.allow_approval || "N",
          title: action.payload.title || state.title,
        };
      })
      // API Request Logs
      .addCase(getApiRequestLogs.pending, (state) => {
        state.apiLogsLoading = true;
        state.apiLogsError = null;
      })
      .addCase(getApiRequestLogs.fulfilled, (state, action) => {
        state.apiLogsLoading = false;
        state.apiLogs = action.payload.results || action.payload;
        console.log(
          "[AdminSlice] apiLogs loaded, first item id:",
          state.apiLogs[0]?.id,
          "total:",
          state.apiLogs.length,
        );
        state.apiLogsError = null;
      })
      .addCase(getApiRequestLogs.rejected, (state, action) => {
        state.apiLogsLoading = false;
        state.apiLogsError =
          action.payload?.detail || "Failed to fetch API logs";
      })
      .addCase(getApiRequestLogDetail.pending, (state) => {
        state.logDetailLoading = true;
        state.logDetailError = null;
      })
      .addCase(getApiRequestLogDetail.fulfilled, (state, action) => {
        state.logDetailLoading = false;
        state.selectedLogDetail = action.payload;
        state.logDetailError = null;
      })
      .addCase(getApiRequestLogDetail.rejected, (state, action) => {
        state.logDetailLoading = false;
        state.logDetailError =
          action.payload?.detail || "Failed to fetch log details";
      })
      // Vendor Categories
      .addCase(getActiveVendorCategories.pending, (state) => {
        state.vendorCategoriesLoading = true;
        state.vendorCategoriesError = null;
      })
      .addCase(getActiveVendorCategories.fulfilled, (state, action) => {
        state.vendorCategoriesLoading = false;
        state.vendorCategories = action.payload.vendor_categories || [];
        state.vendorCategoriesError = null;
      })
      .addCase(getActiveVendorCategories.rejected, (state, action) => {
        state.vendorCategoriesLoading = false;
        state.vendorCategoriesError =
          action.payload?.detail || "Failed to fetch vendor categories";
      })
      .addCase(updateVendorCategories.pending, (state) => {
        state.updateVendorCategoriesLoading = true;
        state.updateVendorCategoriesError = null;
      })
      .addCase(updateVendorCategories.fulfilled, (state, action) => {
        state.updateVendorCategoriesLoading = false;
        state.updateVendorCategoriesError = null;
      })
      .addCase(updateVendorCategories.rejected, (state, action) => {
        state.updateVendorCategoriesLoading = false;
        state.updateVendorCategoriesError =
          action.payload?.detail || "Failed to update vendor categories";
      })
      // User Management
      .addCase(getUserById.pending, (state) => {
        state.selectedUserLoading = true;
        state.selectedUserError = null;
      })
      .addCase(getEmailForTemplate.fulfilled, (state, action) => {
        const newList = action.payload;
        state.templateUsers = newList;
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUser = action.payload;
        state.loginAsError = null;
        state.selectedUserError = null;
        const cartManagementPerms = action.payload.permissions.find(
          (group) => group.category === "Cart Management",
        );
        const orderManagementPerms = action.payload.permissions.find(
          (group) => group.category === "Order Management",
        );
        state.viewCarts = cartManagementPerms.permissions.find(
          (perm) => perm.name === "CanViewCart",
        )?.has_permission;
        state.updateCarts = cartManagementPerms.permissions.find(
          (perm) => perm.name === "CanUpdateCart",
        )?.has_permission;
        state.submitOrders = orderManagementPerms.permissions.find(
          (perm) => perm.name === "CanSubmitOrder",
        )?.has_permission;
      })
      .addCase(getUserById.rejected, (state, action) => {
        state.selectedUserLoading = false;
        state.selectedUserError =
          action.payload?.detail || "Failed to fetch user details";
      })
      // Login As User
      .addCase(loginAsUser.pending, (state) => {
        state.loginAsLoading = true;
        state.loginAsError = null;
        state.loginAsSuccess = false;
      })
      .addCase(loginAsUser.fulfilled, (state, action) => {
        state.loginAsLoading = false;
        state.loginAsSuccess = true;
        state.loginAsError = null;
      })
      .addCase(loginAsUser.rejected, (state, action) => {
        state.loginAsLoading = false;
        state.loginAsSuccess = false;
        state.loginAsError =
          action.payload?.error ||
          action.payload?.detail ||
          "Failed to login as user";
      })
      // Fetch Current Version
      .addCase(fetchCurrentVersion.fulfilled, (state, action) => {
        state.currentVersion = action.payload;
      })
      // Check New Version Available
      .addCase(checkNewVersionAvailable.fulfilled, (state, action) => {
        if (action.payload?.version_outdated) {
          state.updateAvailable = true;
        }
      });
  },
});

export const none = adminSlice.actions;

export default adminSlice.reducer;
